import { Link } from 'react-router-dom'
import { ArrowRight, Clock, Heart, Sparkles, Waves, Flame, Hand, Footprints, Package, CheckCircle, ChevronDown, Loader, Search, X } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase/config'
import toast from 'react-hot-toast'

const Services = () => {
  const [openCategory, setOpenCategory] = useState(null)
  const [categoryServices, setCategoryServices] = useState({}) // { categoryId: [services] }
  const [loadingCategories, setLoadingCategories] = useState({}) // { categoryId: true/false }
  const [categoriesLoaded, setCategoriesLoaded] = useState({}) // Track which categories have been loaded
  const [comboSearchQuery, setComboSearchQuery] = useState('') // Search within combo packages
  const [applyPaystackFeeUplift, setApplyPaystackFeeUplift] = useState(true)
  const [promoDiscount, setPromoDiscount] = useState(0) // when > 0 overrides individual service discount
  const [promoDiscountReason, setPromoDiscountReason] = useState('') // e.g. "New Year" – shown in discount ribbon
  const [allowedVisitTypes, setAllowedVisitTypes] = useState(['spa', 'mobile'])

  // Category configuration – six services + Combo Packages (combinations created in admin)
  const categories = [
    {
      id: 'massage',
      name: 'Massage',
      categoryKey: 'Massage',
      icon: Heart,
      color: 'from-purple-400 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-500'
    },
    {
      id: 'facials',
      name: 'Facials',
      categoryKey: 'Facials',
      icon: Sparkles,
      color: 'from-pink-400 to-pink-600',
      bgColor: 'from-pink-50 to-pink-100',
      borderColor: 'border-pink-200',
      textColor: 'text-pink-500'
    },
    {
      id: 'wax',
      name: 'Wax',
      categoryKey: 'Wax',
      icon: Flame,
      color: 'from-amber-400 to-amber-600',
      bgColor: 'from-amber-50 to-amber-100',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-500'
    },
    {
      id: 'body-scrubbing',
      name: 'Body Scrubbing',
      categoryKey: 'Body Scrubbing',
      icon: Waves,
      color: 'from-amber-400 to-amber-600',
      bgColor: 'from-amber-50 to-amber-100',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-500'
    },
    {
      id: 'manicures',
      name: 'Manicures',
      categoryKey: 'Manicures',
      icon: Hand,
      color: 'from-rose-400 to-rose-600',
      bgColor: 'from-rose-50 to-rose-100',
      borderColor: 'border-rose-200',
      textColor: 'text-rose-500'
    },
    {
      id: 'pedicures',
      name: 'Pedicures',
      categoryKey: 'Pedicures',
      icon: Footprints,
      color: 'from-pink-400 to-rose-500',
      bgColor: 'from-pink-50 to-rose-50',
      borderColor: 'border-pink-200',
      textColor: 'text-pink-500'
    },
    {
      id: 'combo-packages',
      name: 'Combo Packages',
      categoryKey: 'Combo Packages',
      icon: Package,
      color: 'from-purple-400 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-500'
    }
  ]

  // Load Paystack fee uplift flag from Firestore so displayed prices can include it conditionally
  useEffect(() => {
    const loadFeeFlag = async () => {
      try {
        const settingsDocRef = doc(db, 'settings', 'booking')
        const snap = await getDoc(settingsDocRef)
        if (snap.exists()) {
          const data = snap.data()
          if (typeof data.applyPaystackFeeUplift === 'boolean') {
            setApplyPaystackFeeUplift(data.applyPaystackFeeUplift)
          }
          const promo = data.promoDiscount
          if (typeof promo === 'number' && promo >= 0) setPromoDiscount(promo)
          if (typeof data.promoDiscountReason === 'string') setPromoDiscountReason(data.promoDiscountReason)
          if (Array.isArray(data.allowedVisitTypes) && data.allowedVisitTypes.length > 0) {
            setAllowedVisitTypes(data.allowedVisitTypes)
          }
        } else {
          setAllowedVisitTypes(['spa', 'mobile'])
        }
      } catch (e) {
        console.error('Failed to load payment fee settings:', e)
      }
    }
    loadFeeFlag()
  }, [])

  // Fetch services for a category: regular categories from services, Combo from comboPackages
  const fetchCategoryServices = async (categoryKey, categoryId) => {
    if (categoriesLoaded[categoryId]) {
      return
    }

    try {
      setLoadingCategories(prev => ({ ...prev, [categoryId]: true }))
      if (categoryKey === 'Combo Packages') {
        const combosRef = collection(db, 'comboPackages')
        const q = query(combosRef, where('isActive', '==', true))
        const querySnapshot = await getDocs(q)
        const servicesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        setCategoryServices(prev => ({ ...prev, [categoryId]: servicesData }))
        setCategoriesLoaded(prev => ({ ...prev, [categoryId]: true }))
      } else {
        const servicesRef = collection(db, 'services')
        const q = query(
          servicesRef,
          where('isActive', '==', true),
          where('category', '==', categoryKey)
        )
        const querySnapshot = await getDocs(q)
        const servicesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        setCategoryServices(prev => ({ ...prev, [categoryId]: servicesData }))
        setCategoriesLoaded(prev => ({ ...prev, [categoryId]: true }))
      }
    } catch (error) {
      console.error(`Error fetching services for ${categoryKey}:`, error)
      toast.error(`Failed to load ${categoryKey} services. Please try again.`)
      setCategoryServices(prev => ({ ...prev, [categoryId]: [] }))
    } finally {
      setLoadingCategories(prev => ({ ...prev, [categoryId]: false }))
    }
  }

  // Handle URL parameters to auto-open specific category
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const categoryParam = urlParams.get('category')
    
    if (categoryParam) {
      const categoryMap = {
        'massage': 'massage',
        'facials': 'facials',
        'wax': 'wax',
        'body-scrubbing': 'body-scrubbing',
        'manicures': 'manicures',
        'pedicures': 'pedicures',
        'combo-packages': 'combo-packages'
      }
      
      const categoryId = categoryMap[categoryParam.toLowerCase()]
      if (categoryId) {
        const category = categories.find(cat => cat.id === categoryId)
        if (category) {
          setOpenCategory(categoryId)
          fetchCategoryServices(category.categoryKey, categoryId)
          // Scroll to category after a delay
          setTimeout(() => {
            const element = document.getElementById(categoryId)
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }, 300)
        }
      }
    }
  }, [])

  const handleCategoryClick = (category) => {
    const categoryId = category.id
    const wasOpen = openCategory === categoryId
    
    setOpenCategory(wasOpen ? null : categoryId)
    if (wasOpen && categoryId === 'combo-packages') {
      setComboSearchQuery('')
    }
    // Fetch services if opening and not already loaded
    if (!wasOpen && !categoriesLoaded[categoryId]) {
      fetchCategoryServices(category.categoryKey, categoryId)
    }
    
    // Scroll to category header after state update (when opening)
    if (!wasOpen) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          const categoryElement = document.getElementById(categoryId)
          if (categoryElement) {
            // Scroll to the top of the category section
            const elementTop = categoryElement.offsetTop
            const stickyNavHeight = 80 // Approximate height of sticky nav
            const targetPosition = elementTop - stickyNavHeight - 20 // 20px extra spacing
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            })
          }
        }, 100) // Delay to allow content to start expanding
      })
    }
  }

  const getCategoryIconColor = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId)
    return category?.textColor || 'text-green-500'
  }

  // Calculate displayed price (2% increase when enabled)
  const getDisplayedPrice = (price) => {
    if (!price) return 0
    const multiplier = applyPaystackFeeUplift ? 1.02 : 1
    return (price * multiplier).toFixed(2)
  }

  // Calculate discounted price
  const getDiscountedPrice = (price, discount) => {
    if (!price || !discount || discount <= 0) return null
    const displayedPrice = parseFloat(getDisplayedPrice(price))
    const discountedPrice = displayedPrice * (1 - discount / 100)
    return discountedPrice.toFixed(2)
  }

  // Get final price (discounted if available, otherwise displayed price)
  const getFinalPrice = (price, discount) => {
    const discounted = getDiscountedPrice(price, discount)
    return discounted ? parseFloat(discounted) : parseFloat(getDisplayedPrice(price))
  }

  // Filter combo packages by search (name, description, included service names)
  const filterComboServices = (services, query) => {
    if (!query.trim()) return services
    const q = query.toLowerCase().trim()
    return services.filter(service => {
      const nameMatch = service.name?.toLowerCase().includes(q)
      const descMatch = service.description?.toLowerCase().includes(q)
      const serviceNamesMatch = service.serviceNames?.some(name => name?.toLowerCase().includes(q))
      return nameMatch || descMatch || serviceNamesMatch
    })
  }

  return (
    <div className="spa-section min-h-screen">
      <div className="container-spa">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-4">
            Our Services
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Choose from our carefully curated selection of premium spa treatments, 
            all delivered to your home for your ultimate comfort and convenience.
          </p>
        </div>

        {/* Sticky Category Dropdown */}
        <div className="sticky top-16 z-40 mb-6 -mx-4 px-4 md:-mx-8 md:px-8">
          <div className="bg-white/95 backdrop-blur-md shadow-sm rounded-xl border border-purple-200/80 py-2 px-3">
            <label htmlFor="category-select" className="sr-only">Choose category</label>
            <select
              id="category-select"
              value={openCategory ?? ''}
              onChange={(e) => {
                const id = e.target.value
                if (!id) return
                const category = categories.find(c => c.id === id)
                if (category) handleCategoryClick(category)
              }}
              className="w-full appearance-none bg-transparent py-2 pl-4 pr-10 rounded-lg border-0 text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-purple-400/50 cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23934eea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.25rem' }}
            >
              <option value="">Choose a category...</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search bar for Combo Packages */}
        {openCategory === 'combo-packages' && (
          <div className="sticky top-32 z-30 mb-6 -mx-4 px-4 md:-mx-8 md:px-8 bg-white/95 backdrop-blur-md border-b border-purple-200 pb-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search combo packages by name, description, or included services..."
                value={comboSearchQuery}
                onChange={(e) => setComboSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-3 border-2 border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700 placeholder-gray-400"
              />
              {comboSearchQuery && (
                <button
                  type="button"
                  onClick={() => setComboSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Categories with Services */}
        <div className="space-y-3">
          {categories.map((category) => {
            const IconComponent = category.icon
            const allServices = categoryServices[category.id] || []
            const isComboPackages = category.id === 'combo-packages'
            const services = isComboPackages
              ? filterComboServices(allServices, comboSearchQuery)
              : allServices
            const isLoading = loadingCategories[category.id] || false
            return (
              <div key={category.id} className="scroll-mt-20" id={category.id}>
                {/* Category Header - Clickable (slim) */}
                <button
                  onClick={() => handleCategoryClick(category)}
                  className={`w-full mb-2 py-3 px-4 rounded-xl bg-gradient-to-br ${category.bgColor} border ${category.borderColor} shadow-sm hover:shadow-md transition-all duration-200`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`bg-gradient-to-br ${category.color} rounded-lg p-2.5 shadow-sm`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-left">
                        <h2 className="text-xl md:text-2xl font-serif font-bold text-gray-800">
                          {category.name}
                        </h2>
                        <p className="text-gray-600 text-sm mt-0.5">
                          {categoriesLoaded[category.id] 
                            ? isComboPackages
                              ? `${comboSearchQuery ? services.length + ' of ' : ''}${allServices.length} ${allServices.length === 1 ? 'package' : 'packages'} available${comboSearchQuery ? ` (${services.length} matching)` : ''}`
                              : `${allServices.length} ${allServices.length === 1 ? 'service' : 'services'} available`
                            : isComboPackages ? 'Click to view packages' : 'Click to view services'
                          }
                        </p>
                      </div>
                    </div>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
                        openCategory === category.id ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {/* Services Grid - Collapsible */}
                <div 
                  className={`transition-all duration-500 ease-in-out ${
                    openCategory === category.id 
                      ? 'max-h-none opacity-100 overflow-visible' 
                      : 'max-h-0 opacity-0 overflow-hidden'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="w-8 h-8 animate-spin text-green-500 mr-3" />
                      <p className="text-gray-600">Loading services...</p>
                    </div>
                  ) : services.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600 text-lg">
                        {isComboPackages && comboSearchQuery
                          ? `No combo packages found matching "${comboSearchQuery}". Try a different search term.`
                          : 'No services available in this category at the moment. Please check back later.'}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 pb-8">
                      {services.map((service) => {
                        const effectiveDiscount = promoDiscount > 0 ? promoDiscount : (service.discount || 0)
                        return (
                        <div 
                          key={service.id} 
                          className="card bg-white rounded-2xl overflow-visible hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border border-gray-200 relative"
                        >
                          {/* Discount Badge - Bottom Left Edge of Card */}
                          {effectiveDiscount > 0 && (
                            <div className="absolute -bottom-2 -left-2 z-10">
                              <div className="relative w-20 h-20">
                                {/* Red circle background */}
                                <div className="absolute inset-0 bg-red-500 rounded-full shadow-xl"></div>
                                {/* Zigzag border effect using SVG */}
                                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
                                  {/* Create zigzag border by drawing multiple small arcs */}
                                  {Array.from({ length: 32 }, (_, i) => {
                                    const angle1 = (i * 2 * Math.PI) / 32
                                    const angle2 = ((i + 0.5) * 2 * Math.PI) / 32
                                    const angle3 = ((i + 1) * 2 * Math.PI) / 32
                                    const r1 = 36
                                    const r2 = 38
                                    const cx = 40
                                    const cy = 40
                                    const x1 = cx + r1 * Math.cos(angle1)
                                    const y1 = cy + r1 * Math.sin(angle1)
                                    const x2 = cx + r2 * Math.cos(angle2)
                                    const y2 = cy + r2 * Math.sin(angle2)
                                    const x3 = cx + r1 * Math.cos(angle3)
                                    const y3 = cy + r1 * Math.sin(angle3)
                                    return (
                                      <path
                                        key={i}
                                        d={`M ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3}`}
                                        stroke="#dc2626"
                                        strokeWidth="2.5"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    )
                                  })}
                                </svg>
                                {/* Percentage text with "Off" and optional promo reason */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-0.5">
                                  <span className="text-white text-sm font-bold drop-shadow-lg leading-tight">{effectiveDiscount}%</span>
                                  <span className="text-white text-[10px] font-bold drop-shadow-lg leading-tight mt-0.5">OFF</span>
                                  {promoDiscount > 0 && promoDiscountReason.trim() && (
                                    <span className="text-white text-[9px] font-semibold drop-shadow-lg leading-tight mt-0.5 opacity-95 line-clamp-2 max-w-full text-center break-words" title={promoDiscountReason.trim()}>
                                      {promoDiscountReason.trim()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Service Content */}
                          <div className="p-6 relative">
                            {/* Price Badge - Top Right */}
                            <div className={`absolute top-4 right-4 z-10 bg-gradient-to-br ${category.color} text-white px-3 py-1.5 rounded-lg shadow-lg`}>
                              {(() => {
                                // Get prices - support both new (spaPrice/mobilePrice) and old (price) format
                                const spaPrice = service.spaPrice ?? service.price ?? service.amount ?? 0
                                const mobilePrice = service.mobilePrice ?? service.price ?? service.amount ?? 0
                                const showSpa = allowedVisitTypes.includes('spa')
                                const showMobile = allowedVisitTypes.includes('mobile')
                                const hasDiscount = effectiveDiscount > 0
                                
                                if (hasDiscount) {
                                  const spaDisplayed = getDisplayedPrice(spaPrice)
                                  const mobileDisplayed = getDisplayedPrice(mobilePrice)
                                  const spaDiscounted = getDiscountedPrice(spaPrice, effectiveDiscount)
                                  const mobileDiscounted = getDiscountedPrice(mobilePrice, effectiveDiscount)

                                  return (
                                    <div className="flex flex-col items-end text-xs">
                                      {showSpa && (
                                        <div className="mb-1">
                                          <span className="text-[10px] line-through opacity-70">Spa: GHS {spaDisplayed}</span>
                                          <span className="text-sm font-bold ml-1">GHS {spaDiscounted}</span>
                                        </div>
                                      )}
                                      {showMobile && (
                                        <div className={showSpa ? '' : ''}>
                                          <span className="text-[10px] line-through opacity-70">Mobile: GHS {mobileDisplayed}</span>
                                          <span className="text-sm font-bold ml-1">GHS {mobileDiscounted}</span>
                                        </div>
                                      )}
                                      {!showSpa && !showMobile && (
                                        <div className="text-sm font-bold">GHS {spaDiscounted || mobileDiscounted}</div>
                                      )}
                                    </div>
                                  )
                                } else {
                                  return (
                                    <div className="flex flex-col items-end text-xs">
                                      {showSpa && (
                                        <div className="text-sm font-bold">Spa: GHS {getDisplayedPrice(spaPrice)}</div>
                                      )}
                                      {showMobile && (
                                        <div className="text-sm font-bold">Mobile: GHS {getDisplayedPrice(mobilePrice)}</div>
                                      )}
                                      {!showSpa && !showMobile && (
                                        <div className="text-sm font-bold">GHS {getDisplayedPrice(spaPrice || mobilePrice)}</div>
                                      )}
                                    </div>
                                  )
                                }
                              })()}
                            </div>
                            
                            {/* Service Name and Duration */}
                            <div className="mb-4 pr-32">
                              <h3 className="text-xl md:text-2xl font-serif font-bold text-gray-800 mb-2">
                                {service.name}
                              </h3>
                              <div className="flex items-center text-gray-600">
                                <Clock className="w-4 h-4 mr-2" />
                                <span className="text-sm">{service.duration} minutes</span>
                              </div>
                            </div>

                            {/* Description */}
                            <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                              {service.description}
                            </p>
                            
                            {/* Combo Package - Show included services */}
                            {service.isAutoGenerated && service.serviceNames && service.serviceNames.length > 0 && (
                              <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-purple-900 mb-3 uppercase tracking-wide">
                                  Package Includes:
                                </h4>
                                <ul className="space-y-2">
                                  {service.serviceNames.map((serviceName, index) => (
                                    <li key={index} className="flex items-start space-x-2 text-sm text-purple-800">
                                      <Package className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                                      <span>{serviceName}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Includes/Benefits */}
                            <div className="mb-6">
                              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                                Includes & Benefits:
                              </h4>
                              <ul className="space-y-2">
                                {(service.features && service.features.length > 0) ? (
                                  service.features.map((item, index) => (
                                    <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                                      <CheckCircle className={`w-4 h-4 ${getCategoryIconColor(category.id)} flex-shrink-0 mt-0.5`} />
                                      <span>{item}</span>
                                    </li>
                                  ))
                                ) : (
                                  <li className="text-sm text-gray-500 italic">No features listed</li>
                                )}
                              </ul>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                              <Link 
                                to={`/book?category=${encodeURIComponent(category.name)}&type=${encodeURIComponent(service.name)}&duration=${encodeURIComponent(service.duration)}${effectiveDiscount > 0 ? `&discount=${encodeURIComponent(effectiveDiscount)}` : ''}`}
                                className={`flex-1 bg-gradient-to-r ${category.color} hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2`}
                              >
                                <span>Book Now</span>
                                <ArrowRight className="w-4 h-4" />
                              </Link>
                              <Link
                                to={`/gift-card?showSessionForm=true&category=${encodeURIComponent(category.name)}&type=${encodeURIComponent(service.name)}&duration=${encodeURIComponent(service.duration)}${effectiveDiscount > 0 ? `&discount=${encodeURIComponent(effectiveDiscount)}` : ''}`}
                                className="flex-1 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
                              >
                                <Heart className="w-4 h-4" />
                                <span>Generate Session Gift Card</span>
                              </Link>
                            </div>
                          </div>
                        </div>
                      )})}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Services
